import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import OverlayWrapper from './OverlayWrapper';
import noop from 'lodash/noop';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { TriggerEvents, TriggerTargets } from './helpers/consts';

/**
 * @class Tooltip - the basic Tooltip class that is used for all types of overlays.
 * Uses `OverlayWrapper`
 **/
class Tooltip extends Component {
  constructor(props) {
    super(props);
    this.state = {
      show: props.show
    };

    this.getTargetProps = this.getTargetProps.bind(this);
    this.handleHide = this.handleHide.bind(this);
    this.handleShow = this.handleShow.bind(this);
  }

  componentWillUnmount() {
    this._clearShowTimer();
    this._clearHideTimer();
  }

  /**
   * function for getting the binding events on the content element that trigger showing and hiding the popover
   * @param onMouseEnter
   * @param onMouseLeave
   * @param onClick
   * @returns object to be spread on the content element
   */
  getTargetProps({ onMouseEnter = noop, onMouseLeave = noop, onClick = noop } = {}) {
    const externalTargetProps = this.props.getTargetProps();
    const targetProps = {
      ...externalTargetProps,
      className: classNames('Tooltip__target', externalTargetProps.className)
    };
    if ('show' in this.props) {
      return targetProps;
    }

    if (this.props.trigger === TriggerEvents.CLICK) {
      targetProps.onClick = (...args) => {
        !this.state.show && this.handleShow(...args);
        return onClick(...args);
      };
    } else {
      targetProps.onMouseEnter = (...args) => {
        this.handleShow(...args);
        return onMouseEnter(...args);
      };
      targetProps.onMouseLeave = (...args) => {
        this.handleHide(...args);
        return onMouseLeave(...args);
      };
    }
    return targetProps;
  }

  /**
   * shows the Tooltip element
   */

  handleShow() {
    if ('show' in this.props) {
      this.props.onShow();
      return;
    }

    this._clearHideTimer();
    if (this.props.delayShow) {
      this.showTimer && this._clearShowTimer();
      this.showTimer = setTimeout(() => {
        this.setState({ show: true });
        delete this.showTimer;
      }, this.props.delayShow);
      return;
    }
    this.setState({
      show: true
    });
  }

  /**
   * hides the Tooltip element
   */

  handleHide() {
    if ('show' in this.props) {
      this.props.onHide();
      return;
    }

    this._clearShowTimer();
    if (this.props.delayHide) {
      this.hideTimer = setTimeout(() => {
        this.setState({ show: false });
        delete this.hideTimer;
      }, this.props.delayHide);
      return;
    }
    this.setState({
      show: false
    });
  }

  _clearShowTimer() {
    clearTimeout(this.showTimer);
  }

  _clearHideTimer() {
    clearTimeout(this.hideTimer);
  }

  render() {
    // eslint-disable-next-line no-unused-vars
    const { show, content, onShow, rootClose, trigger, ...rest } = this.props;
    const isShow = 'show' in this.props ? show : this.state.show;
    return (
      <OverlayWrapper
        {...rest}
        getTargetProps={this.getTargetProps}
        show={isShow && !!content}
        content={content}
        rootClose={trigger === TriggerEvents.HOVER ? false : rootClose}
        onHide={() => trigger === TriggerEvents.CLICK && this.handleHide()}
      />
    );
  }
}

Tooltip.propTypes = {
  ...OverlayWrapper.propTypes,
  /**
   * A Node, Component instance, or function that returns either. The `container` will have the Tooltip appended to it.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * time in milliseconds to delay the appearance of the popover from the event triggering
   */
  delayShow: PropTypes.number,
  /**
   * time in milliseconds to delay the disappearance of the popover from the event triggering
   */
  delayHide: PropTypes.number,
  /**
   * whether the popover is showed or not. should be sent only for controlled elements
   */
  show: PropTypes.bool,
  /**
   * callback to be called on show
   */
  onShow: PropTypes.func,
  /**
   * callback to be called on hide
   */
  onHide: PropTypes.func,
  /**
   * whether the showing is triggered by click or mouseover
   */
  trigger: PropTypes.oneOf(objectValues(TriggerEvents)),
  /**
   * The Overlay target (either the children or the tooltip itself)
   */
  triggerTarget: PropTypes.oneOf(objectValues(TriggerTargets)),
  /**
   * function getter props for trigger functions
   */
  getTargetProps: PropTypes.func
};

Tooltip.defaultProps = {
  onHide: noop,
  onShow: noop,
  trigger: TriggerEvents.HOVER,
  container: () => document.body,
  triggerTarget: TriggerTargets.CHILDREN,
  getTargetProps: () => ({})
};

export default Tooltip;
